import { Clock, MousePointer2, Flag, RotateCcw, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Match, MODE_LABELS, DIFFICULTY_LABELS, CLICK_LIMITS } from '@/types/game';
import { formatTime } from '@/lib/utils';

interface GameHeaderProps {
  match: Match;
  currentArticle: string;
  clicks: number;
  elapsedTime: number;
  onGiveUp: () => void;
  onRestart: () => void;
  showRestart?: boolean;
  isLoading?: boolean;
}

export function GameHeader({
  match,
  currentArticle,
  clicks,
  elapsedTime,
  onGiveUp,
  onRestart,
  showRestart = true,
  isLoading = false,
}: GameHeaderProps) {
  const clickLimit = match.mode === 'under_clicks' ? CLICK_LIMITS[match.difficulty] : null;
  const isOverLimit = clickLimit ? clicks > clickLimit : false;
  const clicksRemaining = clickLimit ? clickLimit - clicks : null;

  return (
    <header className="game-header">
      <div className="container mx-auto px-4 py-3">
        {/* Route display */}
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-sm font-medium truncate">{match.start_title}</span>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium text-primary truncate">{match.target_title}</span>
          </div>
          
          <div className={`mode-badge shrink-0 ${match.mode === 'time_attack' ? 'time-attack' : 'under-clicks'}`}>
            {MODE_LABELS[match.mode]} · {DIFFICULTY_LABELS[match.difficulty]}
          </div>
        </div>

        {/* Stats and actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Timer */}
            <div className="flex items-center gap-2">
              {isLoading ? (
                <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
              ) : (
                <Clock className="w-4 h-4 text-accent" />
              )}
              <span className={`font-mono text-lg font-semibold tabular-nums ${isLoading ? 'text-muted-foreground' : ''}`}>
                {formatTime(elapsedTime)}
              </span>
              {isLoading && (
                <span className="text-xs text-muted-foreground">paused</span>
              )}
            </div>

            {/* Click counter */}
            <div className="flex items-center gap-2">
              <MousePointer2 className={`w-4 h-4 ${isOverLimit ? 'text-destructive' : 'text-primary'}`} />
              <span className={`font-mono text-lg font-semibold tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>
                {clicks}
                {clickLimit && (
                  <span className="text-muted-foreground text-sm ml-0.5">/{clickLimit}</span>
                )}
              </span>
              {clicksRemaining !== null && clicksRemaining > 0 && clicksRemaining <= 3 && (
                <span className="text-xs text-accent font-medium animate-pulse">
                  {clicksRemaining} left
                </span>
              )}
              {isOverLimit && (
                <span className="text-xs text-destructive font-medium">
                  Over limit
                </span>
              )}
            </div>
          </div>

          {/* Current article - hidden on mobile */}
          <div className="hidden md:block text-sm text-muted-foreground max-w-[200px] truncate">
            {currentArticle}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {showRestart && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRestart}
                className="text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Restart</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onGiveUp}
              className="text-destructive/80 hover:text-destructive hover:bg-destructive/10"
            >
              <Flag className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Give Up</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
